import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  MEMBER_DIAGNOSIS_CREATOR_TYPE,
  MEMBER_DIAGNOSIS_KIND,
  MEMBER_DIAGNOSIS_SEVERITY,
  MEMBER_DIAGNOSIS_SOURCE,
  memberDiagnosisCreatorTypeSchema,
  memberDiagnosisKindSchema,
  memberDiagnosisSeveritySchema,
  memberDiagnosisSourceSchema,
} from './diagnoses';

/**
 * The staff read of a member's substance-use-disorder diagnoses.
 *
 * A hand-authored PROJECTION of `member_diagnoses` (ADR-066 Rule 3, tied back
 * to the table by `member-diagnoses.drift.test.ts`): the display fields only.
 * The DSM-5 criteria, the 1.0 consultation and appointment links and the
 * import provenance stay server-side; `programKey` is derived from the kind so
 * a client places a diagnosis without substance logic of its own.
 */

/**
 * Rows kept per kind, newest first. The bound is per kind, not global, so a
 * long history of one kind can never push another kind's current diagnosis
 * out of the response.
 */
export const MEMBER_DIAGNOSES_PER_KIND_LIMIT = 20;

export const memberDiagnosisSchema = z.object({
  id: z.string().uuid(),
  kind: memberDiagnosisKindSchema,
  programKey: z.string().min(1),
  severity: memberDiagnosisSeveritySchema.nullable(),
  /** The raw 1.0 severity label, kept beside the normalised severity. */
  legacyLabel: z.string().nullable(),
  recordedAt: z.string().datetime({ offset: true }),
  recordedBy: z.object({
    type: memberDiagnosisCreatorTypeSchema.nullable(),
    name: z.string().nullable(),
  }),
  source: memberDiagnosisSourceSchema,
});
export type MemberDiagnosis = z.infer<typeof memberDiagnosisSchema>;

export const memberDiagnosesDataSchema = z.object({
  diagnoses: z.array(memberDiagnosisSchema),
  /** True when any kind held more rows than the per-kind bound returned. */
  truncated: z.boolean(),
});
export type MemberDiagnosesData = z.infer<typeof memberDiagnosesDataSchema>;

export const memberDiagnosesResponseSchema = apiEnvelope(memberDiagnosesDataSchema);
export type MemberDiagnosesResponse = z.infer<typeof memberDiagnosesResponseSchema>;

/** What a client reads in place of a vocabulary value this build does not know. */
export const MEMBER_DIAGNOSIS_UNKNOWN = 'unknown';

/**
 * A string that is one of `values`, or `'unknown'` when it is a string this
 * build has not heard of. The `z.string()` in front keeps the tolerance narrow:
 * a missing field, a number or (where not nullable) a null is a malformed
 * payload, not a newer vocabulary, and still fails the parse.
 */
function tolerantEnum<const T extends readonly [string, ...string[]]>(values: T) {
  const known = (value: string): value is T[number] => values.some((v) => v === value);
  return z
    .string()
    .transform((value): T[number] | typeof MEMBER_DIAGNOSIS_UNKNOWN =>
      known(value) ? value : MEMBER_DIAGNOSIS_UNKNOWN,
    );
}

/**
 * The client-side parse (ADR-135): identical but for the vocabulary fields,
 * which resolve an unrecognised value to `'unknown'` instead of failing, so a
 * kind or severity added server-side does not blank a clinician's view. The
 * server validates its own output through the wire pair above.
 */
export const memberDiagnosisClientSchema = memberDiagnosisSchema.extend({
  kind: tolerantEnum(MEMBER_DIAGNOSIS_KIND),
  severity: tolerantEnum(MEMBER_DIAGNOSIS_SEVERITY).nullable(),
  recordedBy: z.object({
    type: tolerantEnum(MEMBER_DIAGNOSIS_CREATOR_TYPE).nullable(),
    name: z.string().nullable(),
  }),
  source: tolerantEnum(MEMBER_DIAGNOSIS_SOURCE),
});
export type MemberDiagnosisClient = z.infer<typeof memberDiagnosisClientSchema>;

export const memberDiagnosesClientDataSchema = memberDiagnosesDataSchema.extend({
  diagnoses: z.array(memberDiagnosisClientSchema),
});
export type MemberDiagnosesClientData = z.infer<typeof memberDiagnosesClientDataSchema>;

export const memberDiagnosesClientResponseSchema = apiEnvelope(memberDiagnosesClientDataSchema);
